import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, BookOpen, List, PanelLeftOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getBlogPostById } from "@/lib/blog-posts";
import { cn } from "@/lib/utils";

const articleClassName =
  "mx-auto w-full max-w-[900px] min-w-0 font-article text-[1.06rem] leading-[1.88] tracking-normal text-foreground/86 md:text-[1.13rem] [&_.sources-list]:mt-8 [&_.sources-list]:text-[0.9rem] [&_.sources-list]:leading-7 md:[&_.sources-list]:text-[0.96rem] [&_blockquote]:my-8 [&_blockquote]:border-l-4 [&_blockquote]:border-primary/25 [&_blockquote]:pl-5 [&_blockquote]:text-foreground/74 [&_blockquote]:italic [&_code]:rounded-sm [&_code]:bg-stone-200/55 [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.88em] dark:[&_code]:bg-white/8 [&_em]:italic [&_h2]:mb-5 [&_h2]:mt-16 [&_h2]:scroll-mt-28 [&_h2]:font-article [&_h2]:text-[1.8rem] [&_h2]:font-semibold [&_h2]:leading-[1.16] [&_h2]:tracking-normal [&_h2]:text-foreground/94 md:[&_h2]:text-[2.12rem] [&_h3]:mb-3 [&_h3]:mt-10 [&_h3]:scroll-mt-28 [&_h3]:font-article [&_h3]:text-[1.34rem] [&_h3]:font-semibold [&_h3]:leading-[1.24] [&_h3]:text-foreground/92 [&_img]:my-12 [&_img]:w-full [&_img]:rounded-sm [&_img]:border-0 [&_img]:bg-transparent [&_img]:p-0 [&_li]:mt-2 [&_ol]:my-7 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:my-6 [&_pre]:my-9 [&_pre]:overflow-x-auto [&_pre]:whitespace-pre-wrap [&_pre]:break-words [&_pre]:rounded-lg [&_pre]:border [&_pre]:border-stone-300/70 [&_pre]:bg-stone-100/80 [&_pre]:p-4 [&_pre]:font-mono [&_pre]:text-[0.9rem] [&_pre]:leading-7 [&_pre]:text-stone-800 dark:[&_pre]:border-white/10 dark:[&_pre]:bg-white/5 dark:[&_pre]:text-white/85 [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_strong]:font-semibold [&_strong]:text-foreground/92 [&_table]:my-12 [&_table]:w-full [&_table]:border-collapse [&_table]:font-sans [&_table]:text-[0.9rem] [&_tbody_tr]:border-b [&_tbody_tr]:border-border/40 [&_td]:align-top [&_td]:border-b [&_td]:border-border/40 [&_td]:px-4 [&_td]:py-3 [&_th]:border-b [&_th]:border-border/55 [&_th]:bg-stone-100/50 [&_th]:px-4 [&_th]:py-3 [&_th]:text-left [&_th]:font-sans [&_th]:text-[0.78rem] [&_th]:font-semibold [&_th]:tracking-[0.04em] dark:[&_th]:bg-white/5 [&_ul]:my-7 [&_ul]:list-disc [&_ul]:pl-6";

interface TocItem {
  id: string;
  title: string;
  level: 2 | 3;
}

const slugifyHeading = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[`*_~]/g, "")
    .replace(/\[(.*?)\]\(.*?\)/g, "$1")
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "section";

const flattenText = (node: ReactNode): string => {
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node.map(flattenText).join("");
  }

  if (node && typeof node === "object" && "props" in node) {
    return flattenText((node as { props?: { children?: ReactNode } }).props?.children);
  }

  return "";
};

const extractMarkdownTableOfContents = (content?: string): TocItem[] => {
  if (!content) {
    return [];
  }

  const counts = new Map<string, number>();

  return content
    .split("\n")
    .map((line) => line.match(/^(#{2})\s+(.+?)\s*$/))
    .filter(Boolean)
    .map((match) => {
      const level = match![1].length as 2 | 3;
      const title = match![2]
        .replace(/\[(.*?)\]\(.*?\)/g, "$1")
        .replace(/[`*_~]/g, "")
        .trim();
      const baseId = slugifyHeading(title);
      const count = counts.get(baseId) ?? 0;
      counts.set(baseId, count + 1);

      return {
        id: count === 0 ? baseId : `${baseId}-${count + 1}`,
        title,
        level,
      };
    });
};

const getTocDisplayTitle = (title: string) => {
  const findingMatch = title.match(/^Finding\s+(\d+)\s*:/i);
  if (findingMatch) {
    return `Finding ${findingMatch[1]}`;
  }

  if (title.toLowerCase().startsWith("component breakdown")) {
    return "Component Breakdown";
  }

  if (title.toLowerCase() === "the dataset") {
    return "Dataset";
  }

  return title;
};

const createMarkdownComponents = (): Components => {
  const counts = new Map<string, number>();

  const getHeadingId = (children: ReactNode) => {
    const title = flattenText(children).trim();
    const baseId = slugifyHeading(title);
    const count = counts.get(baseId) ?? 0;
    counts.set(baseId, count + 1);
    return count === 0 ? baseId : `${baseId}-${count + 1}`;
  };

  return {
    h2: ({ children }) => <h2 id={getHeadingId(children)}>{children}</h2>,
    h3: ({ children }) => <h3 id={getHeadingId(children)}>{children}</h3>,
    img: ({ src, alt }) => {
      const isWantingLikingImage = src?.includes("wanting-vs-liking.");

      return (
        <img
          src={src ?? ""}
          alt={alt ?? ""}
          className={
            isWantingLikingImage ? "mx-auto my-10 w-full max-w-[760px]" : undefined
          }
        />
      );
    },
  };
};

const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const post = id ? getBlogPostById(id) : undefined;
  const tocItems: TocItem[] = post?.sections?.length
    ? post.sections.map((section) => ({
        id: section.id,
        title: section.title,
        level: 2 as const,
      }))
    : extractMarkdownTableOfContents(post?.content);
  const hasTableOfContents = tocItems.length > 0;
  const tocSignature = tocItems.map((item) => `${item.level}:${item.id}`).join("|");
  const markdownComponents = createMarkdownComponents();
  const [activeSectionId, setActiveSectionId] = useState(tocItems[0]?.id ?? "");
  const [isTocOpen, setIsTocOpen] = useState(false);
  const articleRef = useRef<HTMLElement | null>(null);
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (!element) {
      return;
    }

    const top = element.getBoundingClientRect().top + window.scrollY - 112;
    window.scrollTo({
      top: Math.max(top, 0),
      behavior: "smooth",
    });
  };

  useEffect(() => {
    if (!post) {
      return;
    }

    const previousTitle = document.title;
    const descriptionMeta = document.querySelector(
      'meta[name="description"]',
    ) as HTMLMetaElement | null;
    const previousDescription = descriptionMeta?.getAttribute("content") ?? null;
    let createdMeta = false;
    let metaElement = descriptionMeta;

    if (!metaElement) {
      metaElement = document.createElement("meta");
      metaElement.name = "description";
      document.head.appendChild(metaElement);
      createdMeta = true;
    }

    document.title = `${post.title} | Kishan Vavdara`;
    metaElement.setAttribute("content", post.description);

    return () => {
      document.title = previousTitle;

      if (createdMeta && metaElement?.parentNode) {
        metaElement.parentNode.removeChild(metaElement);
      } else if (metaElement) {
        if (previousDescription === null) {
          metaElement.removeAttribute("content");
        } else {
          metaElement.setAttribute("content", previousDescription);
        }
      }
    };
  }, [post]);

  useEffect(() => {
    if (!tocItems.length) {
      return;
    }

    setActiveSectionId(tocItems[0].id);

    const offset = 180;
    let frameId = 0;
    const getElements = () =>
      tocItems
        .map((section) => document.getElementById(section.id))
        .filter(Boolean) as HTMLElement[];

    const updateActiveSection = () => {
      const elements = getElements();
      if (!elements.length) {
        frameId = 0;
        return;
      }

      const scrollPosition = window.scrollY + offset;
      const reachedBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 24;

      let nextActiveId = elements[0].id;

      for (const element of elements) {
        if (element.offsetTop <= scrollPosition) {
          nextActiveId = element.id;
        } else {
          break;
        }
      }

      if (reachedBottom) {
        nextActiveId = elements[elements.length - 1].id;
      }

      setActiveSectionId((current) =>
        current === nextActiveId ? current : nextActiveId,
      );
      frameId = 0;
    };

    const requestUpdate = () => {
      if (frameId !== 0) {
        return;
      }

      frameId = window.requestAnimationFrame(updateActiveSection);
    };

    requestUpdate();

    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);

    return () => {
      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", requestUpdate);

      if (frameId !== 0) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [tocSignature]);

  useEffect(() => {
    if (!post?.content || post.sections || !articleRef.current || !tocItems.length) {
      return;
    }

    const headings = Array.from(articleRef.current.querySelectorAll("h2"));
    headings.forEach((heading, index) => {
      const item = tocItems[index];
      if (item) {
        heading.id = item.id;
      }
    });
  }, [post?.content, post?.sections, tocSignature, tocItems]);

  useEffect(() => {
    if (!articleRef.current) {
      return;
    }

    articleRef.current.querySelectorAll(".sources-list").forEach((node) => {
      node.classList.remove("sources-list");
    });

    const paragraphs = Array.from(articleRef.current.querySelectorAll("p"));
    for (const paragraph of paragraphs) {
      const text = paragraph.textContent?.trim().replace(/\s+/g, " ");
      if (text === "Sources:" || text === "Sources") {
        const next = paragraph.nextElementSibling;
        if (next instanceof HTMLUListElement) {
          next.classList.add("sources-list");
        }
      }
    }
  }, [post?.content, post?.sections]);

  useEffect(() => {
    if (!tocItems.length || !window.location.hash) {
      return;
    }

    const hashId = window.location.hash.replace(/^#/, "");
    if (!hashId) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      scrollToSection(hashId);
      setActiveSectionId(hashId);
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [tocSignature]);

  if (!post) {
    return (
      <div className="min-h-screen container mx-auto px-4 py-12">
        <Button
          variant="ghost"
          className="mb-8 flex items-center gap-2"
          onClick={() => navigate("/blog")}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Blog
        </Button>

        <div className="flex flex-col items-center justify-center py-12 text-center">
          <BookOpen className="mb-6 h-24 w-24 text-primary" />
          <h1 className="mb-4 text-4xl font-bold">Blog post not found</h1>
          <p className="max-w-2xl text-xl text-muted-foreground">
            The article you are looking for does not exist or has been moved.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen container mx-auto px-4 py-8 md:py-10">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={cn(
          "mx-auto max-w-[1320px]",
          hasTableOfContents
            ? cn(
                "lg:grid lg:gap-10 xl:gap-14",
                isTocOpen
                  ? "lg:grid-cols-[240px_minmax(0,1fr)]"
                  : "lg:grid-cols-[48px_minmax(0,1fr)]",
              )
            : "max-w-[980px]",
        )}
      >
        <div className="min-w-0 space-y-6 lg:col-start-2">
          <div className="mx-auto w-full max-w-[900px]">
            <Button
              variant="ghost"
              className="-ml-2 flex items-center gap-2"
              onClick={() => navigate("/blog")}
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Blog
            </Button>
          </div>

          <header className="mx-auto w-full max-w-[900px] space-y-5 border-b border-border/45 pb-8 dark:border-transparent">
            <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground md:text-[13px]">
              <span>{post.date}</span>
              <span>{post.readingTime}</span>
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-border/50 px-2 py-0.5 text-[10px] font-medium text-foreground/85 md:text-[11px]"
                >
                  {tag}
                </span>
              ))}
            </div>
            <h1
              className="max-w-none font-article text-[2rem] font-semibold leading-[1.08] tracking-normal text-foreground md:text-[2.55rem] lg:text-[3rem]"
            >
              {post.title}
            </h1>
            {post.excerpt && (
              <p className="max-w-3xl text-[1rem] leading-8 text-muted-foreground md:text-[1.08rem]">
                {post.excerpt}
              </p>
            )}
          </header>

          <article ref={articleRef} className={articleClassName}>
            {post.intro?.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}

            {post.sections?.map((section) => (
              <section
                key={section.id}
                id={section.id}
                className="scroll-mt-28 border-t border-border/40 pt-10 first:border-t-0 first:pt-4"
              >
                <h2 className="mb-4 font-sans text-2xl font-semibold tracking-tight text-foreground md:text-[2rem]">
                  {section.title}
                </h2>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={markdownComponents}
                >
                  {section.content}
                </ReactMarkdown>

                {section.id === "how-to-choose" && post.comparisonRows && (
                  <>
                    <div className="mt-6 rounded-2xl border border-border/55 bg-background/45 px-4 py-3 text-sm leading-7 text-muted-foreground">
                      Jump to the deep dives below if you want the reasoning
                      behind each choice.
                    </div>

                    <div className="mt-8 rounded-3xl border border-border/60 bg-background/50 p-5 shadow-sm">
                      <p className="text-sm leading-7 text-muted-foreground">
                        Numbers will vary by model, task, and dataset size -
                        treat these as directional, not absolute.
                      </p>

                      <div className="mt-5 overflow-x-auto">
                        <table className="w-full min-w-[720px] border-separate border-spacing-0 text-left text-sm">
                          <thead>
                            <tr className="text-foreground">
                              <th className="border-b border-border/60 px-4 py-3 font-semibold">
                                Method
                              </th>
                              <th className="border-b border-border/60 px-4 py-3 font-semibold">
                                Trainable %
                              </th>
                              <th className="border-b border-border/60 px-4 py-3 font-semibold">
                                Convergence Speed
                              </th>
                              <th className="border-b border-border/60 px-4 py-3 font-semibold">
                                Memory Overhead
                              </th>
                              <th className="border-b border-border/60 px-4 py-3 font-semibold">
                                Best For
                              </th>
                              <th className="border-b border-border/60 px-4 py-3 font-semibold">
                                Failure Risk
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {post.comparisonRows.map((row) => (
                              <tr
                                key={row.method}
                                className="align-top text-muted-foreground"
                              >
                                <td className="border-b border-border/40 px-4 py-4 font-semibold text-foreground">
                                  {row.method}
                                </td>
                                <td className="border-b border-border/40 px-4 py-4">
                                  {row.trainablePct}
                                </td>
                                <td className="border-b border-border/40 px-4 py-4">
                                  {row.convergenceSpeed}
                                </td>
                                <td className="border-b border-border/40 px-4 py-4">
                                  {row.memoryOverhead}
                                </td>
                                <td className="border-b border-border/40 px-4 py-4">
                                  {row.bestFor}
                                </td>
                                <td className="border-b border-border/40 px-4 py-4">
                                  {row.failureRisk}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}

                {section.id === "what-each-method-changes" && (
                  <div className="mt-8 grid gap-4 md:grid-cols-3">
                      <div className="rounded-2xl border border-border/55 bg-background/55 p-5 shadow-sm">
                        <p className="text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                          LoRA
                        </p>
                        <div className="mt-4 rounded-xl border border-border/50 bg-background/70 p-4 font-mono text-sm text-foreground">
                          y = (W + BA)x
                        </div>
                        <p className="mt-3 text-sm leading-7 text-muted-foreground">
                          Additive patch over the frozen weight matrix.
                        </p>
                      </div>

                      <div className="rounded-2xl border border-border/55 bg-background/55 p-5 shadow-sm">
                        <p className="text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                          IA3
                        </p>
                        <div className="mt-4 rounded-xl border border-border/50 bg-background/70 p-4 font-mono text-sm text-foreground">
                          y = W(l ⊙ x)
                        </div>
                        <p className="mt-3 text-sm leading-7 text-muted-foreground">
                          Rescales internal activations instead of patching
                          weights.
                        </p>
                      </div>

                      <div className="rounded-2xl border border-border/55 bg-background/55 p-5 shadow-sm">
                        <p className="text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                          BOFT
                        </p>
                        <div className="mt-4 rounded-xl border border-border/50 bg-background/70 p-4 font-mono text-sm text-foreground">
                          y = RWx
                        </div>
                        <p className="mt-3 text-sm leading-7 text-muted-foreground">
                          Applies a structured multiplicative transform to the
                          frozen weights.
                        </p>
                      </div>
                  </div>
                )}
              </section>
            ))}

            {post.content && !post.sections && (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={markdownComponents}
              >
                {post.content}
              </ReactMarkdown>
            )}
          </article>
        </div>

        {hasTableOfContents && (
          <aside className="hidden lg:col-start-1 lg:row-start-1 lg:block">
            <div
              className={cn(
                "sticky top-28 pt-1",
                isTocOpen ? "w-full" : "flex justify-end",
              )}
            >
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={isTocOpen ? "Collapse table of contents" : "Expand table of contents"}
                aria-expanded={isTocOpen}
                onClick={() => setIsTocOpen((open) => !open)}
                className={cn(
                  "h-9 w-9 rounded-full border border-border/45 bg-background/80 text-muted-foreground shadow-sm backdrop-blur hover:text-foreground",
                  isTocOpen && "mb-1",
                )}
              >
                {isTocOpen ? (
                  <PanelLeftOpen className="h-4 w-4" />
                ) : (
                  <List className="h-4 w-4" />
                )}
              </Button>

              {isTocOpen && (
                <div className="mt-5">
                  <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    On this page
                  </p>
                  <nav className="mt-4 space-y-1">
                    {tocItems.map((section) => (
                      <a
                        key={section.id}
                        href={`#${section.id}`}
                        onClick={(event) => {
                          event.preventDefault();
                          setActiveSectionId(section.id);
                          window.history.replaceState(null, "", `#${section.id}`);
                          scrollToSection(section.id);
                        }}
                        className={cn(
                          "block border-l border-border/40 py-1.5 pl-4 pr-2 text-[12px] leading-5 text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground",
                          section.level === 3 && "pl-7 text-[11px]",
                          activeSectionId === section.id &&
                            "border-foreground/60 font-medium text-foreground",
                        )}
                      >
                        {getTocDisplayTitle(section.title)}
                      </a>
                    ))}
                  </nav>
                </div>
              )}
            </div>
          </aside>
        )}
      </motion.div>
    </div>
  );
};

export default BlogPost;
